import React, { useState, useCallback } from 'react'
import { Button, Input, Switch, Tooltip } from '@heroui/react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { DebouncedInput } from './@components/DebouncedInput'
import useDataEvent from './CustomHook'

/**
 * CustomFieldsEditor
 *
 * Renders a section in the sidebar that lets users add/remove custom
 * fields and flags for primitives whose category has extendable: true.
 *
 * Field definitions (name, type) are stored on the top-level task
 * primitive (board / analysis / activity / experiment) keyed by
 * referenceId so that different primitives sharing the same
 * referenceId see the same fields.
 *
 * Field values are stored per-primitive in
 * referenceParameters.custom_{id}.
 */
export default function CustomFieldsEditor({ primitive }) {
  const [newFieldName, setNewFieldName] = useState('')
  const [newFieldType, setNewFieldType] = useState('text')
  const [adding, setAdding] = useState(false)

  const task = primitive?.task
  const refId = primitive?.referenceId

  // Re-render when the task definitions change or the primitive values change
  useDataEvent('set_field set_parameter', [task?.id, primitive?.id].filter(Boolean))

  const fieldKey = `cf_${refId}`

  // Field definitions from the task (shared across same referenceId)
  const fieldDefs = (task?.customFields && task.customFields[fieldKey]) || []

  // Derive next internal id (custom_0, custom_1, ...)
  const nextId = useCallback(() => {
    let max = -1
    for (const f of fieldDefs) {
      if (f && f.id && typeof f.id === 'string' && f.id.startsWith('custom_')) {
        const n = parseInt(f.id.slice(7), 10)
        if (!isNaN(n) && n > max) max = n
      }
    }
    return `custom_${max + 1}`
  }, [fieldDefs])

  // Add a new field definition to the task
  const handleAdd = () => {
    const name = newFieldName.trim()
    if (!name || !task) return

    const id = nextId()
    const newDefs = [...fieldDefs, { id, name, type: newFieldType }]

    // Persist definitions onto the task primitive
    task.setField(`customFields.${fieldKey}`, newDefs)

    setNewFieldName('')
    setAdding(false)
  }

  // Update a field value on the current primitive
  const handleUpdateValue = (fieldId, newValue) => {
    if (!primitive) return
    primitive.setField(`referenceParameters.${fieldId}`, newValue)
  }

  // Remove a field definition from the task
  const handleRemove = (index) => {
    if (!task) return
    const def = fieldDefs[index]
    const updated = fieldDefs.filter((_, i) => i !== index)
    task.setField(`customFields.${fieldKey}`, updated)

    // Also clean up the value on the current primitive
    if (def && primitive) {
      primitive.setField(`referenceParameters.${def.id}`, undefined)
    }
  }

  if (!task || !refId) return null

  return (
    <div className="px-4 pb-4">
      <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
            Custom Fields
          </h3>
          <Button
            size="sm"
            variant="light"
            isIconOnly
            onPress={() => setAdding(!adding)}
            aria-label="Add custom field"
          >
            <PlusIcon className="w-4 h-4" />
          </Button>
        </div>

        {/* Add new field form */}
        {adding && (
          <div className="mb-3 p-3 border border-gray-200 rounded-lg dark:border-gray-700 space-y-2">
            <Input
              size="sm"
              label="Field name"
              placeholder="e.g. Priority"
              value={newFieldName}
              onValueChange={setNewFieldName}
            />
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Type:</span>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="customFieldType"
                  value="text"
                  checked={newFieldType === 'text'}
                  onChange={() => setNewFieldType('text')}
                />
                Text
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="customFieldType"
                  value="flag"
                  checked={newFieldType === 'flag'}
                  onChange={() => setNewFieldType('flag')}
                />
                Flag
              </label>
            </div>
            <Button
              size="sm"
              color="primary"
              variant="flat"
              onPress={handleAdd}
              isDisabled={!newFieldName.trim()}
              className="w-full"
            >
              Add
            </Button>
          </div>
        )}

        {/* Existing custom fields */}
        {fieldDefs.length > 0 && (
          <div className="space-y-3">
            {fieldDefs.map((field, index) => {
              const value = primitive?.referenceParameters?.[field.id]
              return (
                <div
                  key={field.id || index}
                  className="flex items-start gap-2 p-2 border border-gray-100 rounded-lg dark:border-gray-800"
                >
                  <div className="flex-1 min-w-0">
                    <label className="text-xs font-medium text-gray-400 dark:text-gray-500 block mb-1 truncate">
                      {field.name}
                    </label>
                    {field.type === 'flag' ? (
                      <Switch
                        size="sm"
                        isSelected={!!value}
                        onValueChange={(next) => handleUpdateValue(field.id, next)}
                      />
                    ) : (
                      <DebouncedInput
                        size="sm"
                        variant="bordered"
                        value={value || ''}
                        onChange={(next) => handleUpdateValue(field.id, next)}
                        debounce={300}
                        placeholder={`Value for ${field.name}`}
                      />
                    )}
                  </div>
                  <Tooltip content="Remove field" placement="left">
                    <Button
                      size="sm"
                      variant="light"
                      color="danger"
                      isIconOnly
                      onPress={() => handleRemove(index)}
                      className="mt-4"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </Tooltip>
                </div>
              )
            })}
          </div>
        )}

        {fieldDefs.length === 0 && !adding && (
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">
            No custom fields defined. Click + to add one.
          </p>
        )}
      </div>
    </div>
  )
}
